'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import type { Bot, BotMessage } from '@/types/bot';
import { BotChatMessage } from './BotChatMessage';
import { getBotAvatarSrc } from './getBotAvatarSrc';

interface BotChatWindowProps {
  messages: BotMessage[];
  bot: Bot;
  isLoading: boolean;
  isSending: boolean;
  isStreaming?: boolean;
  streamingContent?: string;
  className?: string;
}

function MessageSkeleton() {
  return (
    <div className="flex gap-3 px-4 py-2">
      <Skeleton className="h-10 w-10 shrink-0 rounded-full bg-[color:rgba(255,255,255,0.08)]" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-24 bg-[color:rgba(255,255,255,0.08)]" />
        <Skeleton className="h-16 w-64 rounded-2xl bg-[color:rgba(255,255,255,0.08)]" />
        <Skeleton className="h-3 w-12 bg-[color:rgba(255,255,255,0.08)]" />
      </div>
    </div>
  );
}

function TypingIndicator({ bot }: { bot: Bot }) {
  const avatarSrc = getBotAvatarSrc(bot);

  return (
    <div className="flex gap-3 px-4 py-2" data-testid="typing-indicator">
      <div className="w-10 shrink-0">
        <Avatar className="jr-avatar-ring h-10 w-10">
          <AvatarImage src={avatarSrc} alt={bot.name} />
          <AvatarFallback className="bg-[color:var(--jr-surface-muted)] font-semibold text-[color:var(--jr-accent)]">
            {bot.name.charAt(0)}
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="flex flex-col items-start">
        <span className="mb-1 text-sm font-medium text-[var(--jr-text)]">{bot.name}</span>
        <div className="jr-chat-bubble-bot">
          <div className="flex gap-1">
            <span className="h-2 w-2 animate-bounce rounded-full bg-[color:var(--jr-accent)]/50" style={{ animationDelay: '0ms' }} />
            <span className="h-2 w-2 animate-bounce rounded-full bg-[color:var(--jr-accent)]/50" style={{ animationDelay: '150ms' }} />
            <span className="h-2 w-2 animate-bounce rounded-full bg-[color:var(--jr-accent)]/50" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function StreamingMessage({ bot, content }: { bot: Bot; content: string }) {
  const avatarSrc = getBotAvatarSrc(bot);

  return (
    <div className="flex gap-3 px-4 py-2" data-testid="streaming-message">
      <div className="w-10 shrink-0">
        <Avatar className="jr-avatar-ring h-10 w-10">
          <AvatarImage src={avatarSrc} alt={bot.name} />
          <AvatarFallback className="bg-[color:var(--jr-surface-muted)] font-semibold text-[color:var(--jr-accent)]">
            {bot.name.charAt(0)}
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="flex flex-col items-start max-w-[75%]">
        <span className="mb-1 text-sm font-medium text-[var(--jr-text)]">{bot.name}</span>
        <div className="jr-chat-bubble-bot">
          <p className="text-sm whitespace-pre-wrap leading-relaxed text-[var(--jr-text)]">
            {content}
            <span className="ml-0.5 inline-block h-4 w-2 animate-pulse bg-[color:var(--jr-accent)]/70" />
          </p>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ bot }: { bot: Bot }) {
  const persona = bot.persona;
  const avatarSrc = getBotAvatarSrc(bot);

  return (
    <div
      className="flex flex-col items-center justify-center h-full px-6 py-8 text-center"
      data-testid="bot-chat-empty"
    >
      <Avatar className="jr-avatar-ring mb-4 h-16 w-16">
        <AvatarImage src={avatarSrc} alt={bot.name} className="object-cover" />
        <AvatarFallback className="bg-[color:var(--jr-surface-muted)] text-2xl font-bold text-[color:var(--jr-accent)]">
          {bot.name.charAt(0)}
        </AvatarFallback>
      </Avatar>
      <h2 className="jr-display mb-2 text-4xl font-semibold">Chat with {bot.name}</h2>
      <p className="mb-6 max-w-sm text-sm text-[var(--jr-text-soft)]">
        {persona.description}
      </p>

      {/* Suggested conversation starters */}
      {persona.greeting_messages.length > 0 && (
        <div className="w-full max-w-sm">
          <p className="mb-3 text-xs text-[var(--jr-text-muted)]">Try starting with:</p>
          <div className="space-y-2">
            {persona.greeting_messages.slice(0, 3).map((greeting, index) => (
              <div
                key={index}
                className="jr-card-muted rounded-2xl px-4 py-3 text-left text-sm text-[var(--jr-text)]"
              >
                "{greeting}"
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function BotChatWindow({
  messages,
  bot,
  isLoading,
  isSending,
  isStreaming = false,
  streamingContent = '',
  className,
}: BotChatWindowProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior });
  }, []);

  // Auto-scroll when new messages arrive, typing starts, or streaming content updates
  useEffect(() => {
    scrollToBottom('smooth');
  }, [messages.length, isSending, isStreaming, streamingContent, scrollToBottom]);

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex flex-col h-full', className)} data-testid="bot-chat-loading">
        <div className="flex-1 overflow-hidden p-2">
          {[...Array(4)].map((_, i) => (
            <MessageSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (messages.length === 0 && !isSending) {
    return (
      <div className={cn('flex flex-col h-full', className)}>
        <EmptyState bot={bot} />
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className={cn('flex-1 overflow-y-auto bg-transparent', className)}
      data-testid="bot-chat-window"
    >
      {/* Messages */}
      <div className="py-4">
        {messages.map((message) => (
          <BotChatMessage
            key={message.id}
            message={message}
            bot={bot}
            isUser={message.role === 'user'}
          />
        ))}

        {/* Streaming message while content is arriving */}
        {isStreaming && streamingContent && (
          <StreamingMessage bot={bot} content={streamingContent} />
        )}

        {/* Typing indicator when waiting for response to start */}
        {isSending && !isStreaming && <TypingIndicator bot={bot} />}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}
